import {
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { encryptStorage } from '../utils/encryptStorage';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GetUserPreferencesService } from '../services/profile/profileService';
import { useTranslation } from 'react-i18next';

export const AuthContext = createContext(
  undefined,
);

const AUTH_APP = 'AUTH_APP';
const USER = 'USER';

function AuthContextProvider({
  children,
}) {
  /* Hooks */
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  /* States */
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return encryptStorage.getItem(AUTH_APP) ? true : false;
    } catch {
      encryptStorage.clear();
      return false;
    }
  });
  const [user, setUser] = useState(() => {
    try {
      return encryptStorage.getItem(USER) || null;
    } catch {
      encryptStorage.clear();
      return null;
    }
  });

  const { data: preferencesData } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => GetUserPreferencesService({ token: user?.access_token }),
    enabled: !!user?.access_token,
  });

  const preferences = preferencesData?.data?.data

  /* Effects */
  useEffect(() => {
    // check expiration date, and logout if expired
    try {
      const expirationDate = encryptStorage.getItem('expirationDate');
      if (expirationDate) {
        const now = new Date();
        if (now.getTime() > new Date(expirationDate)) {
          logout();
        }
      }
    } catch {
      encryptStorage.clear();
      logout();
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      queryClient.removeQueries(query => {
        const queriesToRemove = [
          'notification-list',
          'contacts-by-user',
          "contactsRechargesByUser",
          "user-invitation"
        ];
        return queriesToRemove.some(v => query.queryKey.includes(v));
      })
      setUser(null);
      encryptStorage.removeItem(AUTH_APP);
      encryptStorage.removeItem(USER);
      encryptStorage.removeItem('expirationDate');
    }
  }, [isAuthenticated]);

  // effect language
  useEffect(() => {
    if (preferences?.language && i18n.language !== preferences.language) {
      i18n.changeLanguage(preferences.language);
    }
  }, [preferences?.language]);

  /* Fn */
  const login = useCallback((user) => {
    setUser(user);
    encryptStorage.setItem(AUTH_APP, true);
    encryptStorage.setItem(USER, JSON.stringify(user));
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
  }, []);

  const updateUser = useCallback((newUser) => {
    encryptStorage.setItem(USER, JSON.stringify(newUser));
    setUser(newUser);
    encryptStorage.setItem(AUTH_APP, true);
    setIsAuthenticated(true);
  }, []);

  const value = useMemo(
    () => ({
      login,
      logout,
      updateUser,
      isAuthenticated,
      user,
      preferences,
    }),
    [login, logout, updateUser, isAuthenticated, user, preferences],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default AuthContextProvider;
